<template>
    <div class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm h-full flex flex-col">
        <div class="flex items-start justify-between">
            <div>
                <div
                    class="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold">
                    🏫 Student Life
                </div>
                <h3 class="mt-4 text-2xl font-extrabold text-indigo-900">Featured Clubs & Activities</h3>
                <p class="mt-2 text-gray-600">Clubs created by {{ userName || 'this user' }} — explore highlights and
                    participation.</p>
            </div>
        </div>

        <div v-if="isLoading && items.length === 0" class="flex-1 grid place-items-center">
            <div class="flex items-center space-x-3 text-gray-500">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span class="text-sm">Loading Clubs...</span>
            </div>
        </div>

        <div v-else-if="items.length === 0" class="flex-1 grid place-items-center">
            <div class="text-center">
                <div
                    class="w-24 h-24 mx-auto rounded-full bg-indigo-50 border border-indigo-100 grid place-items-center">
                    <span class="text-3xl">🏫</span>
                </div>
                <h3 class="mt-5 text-lg font-semibold text-indigo-900">No Clubs yet</h3>
                <p class="mt-1 text-sm text-gray-600">Activities created by {{ userName || 'this user' }} will appear
                    here.</p>
                <div class="mt-4">
                    <button @click="goToStudentLife"
                        class="px-4 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm transition">Create
                        Activity</button>
                </div>
            </div>
        </div>

        <div v-else class="mt-6 flex flex-col">
            <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Activities</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ metricActivities }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Participants</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ metricParticipants }}</p>
                </div>
                <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-500">Total Posts</p>
                    <p class="mt-1 text-3xl font-extrabold text-indigo-900">{{ metricPosts }}</p>
                </div>
            </div>

            <div class="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div v-for="item in items" :key="item.token"
                    class="rounded-xl border border-gray-200 overflow-hidden bg-white hover:shadow-md transition flex flex-col">
                    <div class="h-32 bg-indigo-50 border-b border-gray-100 grid place-items-center">
                        <img v-if="item.thumbnail_url" :src="getImageUrl(item.thumbnail_url)" alt="thumbnail"
                            class="w-full h-full object-cover" />
                        <span v-else class="text-4xl">🎯</span>
                    </div>
                    <div class="p-4 flex-1 flex flex-col">
                        <div class="flex items-start justify-between">
                            <span class="text-sm font-semibold text-gray-700 truncate w-full">{{ item.title }}</span>
                            <span class="text-xs text-gray-400 whitespace-nowrap">{{ formatDate(item.created_at)
                                }}</span>
                        </div>
                        <p class="mt-2 text-sm text-gray-600 line-clamp-3">{{ item.description }}</p>
                        <div class="mt-3 flex items-center justify-between text-xs">
                            <span class="text-gray-500">Participants: {{ item.participants_count || 0 }}</span>
                            <button @click.stop="openActivity(item.token)"
                                class="px-3 py-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition">Open</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isLoading && items.length > 0" class="text-center py-4 text-gray-500">Loading more...</div>
        <div v-if="!hasMore && items.length > 0" class="text-center py-4 text-gray-400">No more items</div>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const LIST_USER_STUDENT_LIFE = gql`
  query ListUserStudentLife($userToken: String, $first: Int!, $page: Int!) {
    listUserStudentLife(userToken: $userToken, first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo { hasMorePages currentPage total }
      activities_total
      participants_total
      posts_total
      data {
        token
        title
        description
        thumbnail_url
        participants_count
        created_at
      }
    }
  }
`;

export default {
    name: "StudentLife",
    props: {
        token: { type: String, default: "" },
        userName: { type: String, default: "" }
    },
    data() {
        return {
            items: [],
            isLoading: false,
            first: 6,
            page: 1,
            hasMore: true,
            metricActivities: 0,
            metricParticipants: 0,
            metricPosts: 0,
        };
    },
    mounted() {
        this.fetchStudentLife();
        this.$nextTick(() => {
            const el = document.getElementById('dashboard-scroll-container');
            if (el) el.addEventListener('scroll', this.handleScroll);
        });
    },
    beforeUnmount() {
        const el = document.getElementById('dashboard-scroll-container');
        if (el) el.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        getImageUrl(url) {
            if (!url) return '';
            if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) return url;
            return (process.env.VUE_APP_PUBLIC_URL || '') + url;
        },
        async fetchStudentLife() {
            if (this.isLoading || !this.hasMore) return;
            this.isLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: LIST_USER_STUDENT_LIFE,
                    variables: { userToken: this.token || null, first: this.first, page: this.page },
                    fetchPolicy: 'network-only'
                });
                const payload = data && data.listUserStudentLife;
                if (payload && !payload.error) {
                    const list = payload.data || [];
                    if (this.page === 1) this.items = list; else this.items = this.items.concat(list);
                    this.hasMore = payload.paginatorInfo && payload.paginatorInfo.hasMorePages;
                    this.metricActivities = (payload.activities_total != null ? payload.activities_total : (payload.paginatorInfo && payload.paginatorInfo.total) || 0);
                    this.metricParticipants = payload.participants_total || 0;
                    this.metricPosts = payload.posts_total || 0;
                }
            } finally {
                this.isLoading = false;
            }
        },
        handleScroll(e) {
            const el = e.target;
            const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (bottomGap < 200 && this.hasMore && !this.isLoading) {
                this.page += 1;
                this.fetchStudentLife();
            }
        },
        openActivity(token) {
            if (token) {
                this.$router.push(`/student-life/${token}`);
            }
        },
        goToStudentLife() {
            this.$router.push('/student-life');
        },
        formatDate(val) {
            if (!val) return '';
            try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch (e) { return ''; }
        },
    }
}
</script>

<style lang="scss" scoped></style>
